/**
 * Smart Code Generation - Context-aware AI prompt generation and code optimization
 * 
 * This service creates intelligent, context-aware prompts for AI code generation
 * based on user editing patterns, project context, and conversation history.
 * It optimizes code generation accuracy and relevance through sophisticated
 * prompt engineering and context analysis.
 */

import timelineStateManager from './timelineStateManager.js';
import timelineQuerySystem from './timelineQuerySystem.js';

class SmartCodeGenerator {
  constructor() {
    this.promptTemplates = new Map();
    this.contextAnalyzers = new Map();
    this.codeOptimizers = new Map();
    this.generationHistory = [];
    
    // Initialize built-in templates and analyzers
    this._initializePromptTemplates();
    this._initializeContextAnalyzers();
    this._initializeCodeOptimizers();
  }

  /**
   * Generate context-aware prompt for AI code generation
   */
  generateContextAwarePrompt(userRequest, timelineContext, options = {}) {
    try {
      // Analyze the current context
      const contextAnalysis = this._analyzeFullContext(userRequest, timelineContext);
      
      // Determine the best prompt strategy
      const promptStrategy = this._selectPromptStrategy(contextAnalysis, options);
      
      // Generate the enhanced prompt
      const enhancedPrompt = this._buildEnhancedPrompt(userRequest, contextAnalysis, promptStrategy);
      
      // Add context-specific instructions
      const finalPrompt = this._addContextInstructions(enhancedPrompt, contextAnalysis);
      
      // Record generation for learning
      this._recordGeneration(userRequest, contextAnalysis, promptStrategy, finalPrompt);
      
      return {
        prompt: finalPrompt,
        strategy: promptStrategy,
        contextAnalysis,
        confidence: this._calculatePromptConfidence(contextAnalysis, promptStrategy),
        estimatedComplexity: this._estimateTaskComplexity(contextAnalysis),
        suggestedModel: this._suggestAIModel(contextAnalysis)
      };

    } catch (error) {
      console.error('Smart code generation failed:', error);
      return this._generateFallbackPrompt(userRequest, timelineContext);
    }
  }

  /**
   * Generate smart code suggestions based on context
   */
  generateSmartSuggestions(timelineContext, options = {}) {
    const contextAnalysis = this._analyzeFullContext('', timelineContext);
    const suggestions = [];

    // Pattern-based suggestions
    const patternSuggestions = this._generatePatternBasedSuggestions(contextAnalysis);
    suggestions.push(...patternSuggestions);

    // Content-aware suggestions
    const contentSuggestions = this._generateContentAwareSuggestions(contextAnalysis);
    suggestions.push(...contentSuggestions);

    // Optimization suggestions
    const optimizationSuggestions = this._generateOptimizationSuggestions(contextAnalysis);
    suggestions.push(...optimizationSuggestions);

    // User preference suggestions
    const preferenceSuggestions = this._generatePreferenceBasedSuggestions(contextAnalysis);
    suggestions.push(...preferenceSuggestions);

    return this._rankAndFilterSuggestions(suggestions, options);
  }

  /**
   * Optimize generated code based on context
   */
  optimizeGeneratedCode(code, originalRequest, timelineContext) {
    const contextAnalysis = this._analyzeFullContext(originalRequest, timelineContext);
    
    let optimizedCode = code;

    // Apply context-specific optimizations
    for (const [optimizerName, optimizer] of this.codeOptimizers) {
      if (optimizer.shouldApply(contextAnalysis)) {
        optimizedCode = optimizer.optimize(optimizedCode, contextAnalysis);
      }
    }

    // Add safety checks based on context
    optimizedCode = this._addContextSafetyChecks(optimizedCode, contextAnalysis);

    // Add error handling if needed
    optimizedCode = this._addSmartErrorHandling(optimizedCode, contextAnalysis);

    return {
      code: optimizedCode,
      optimizations: this._getAppliedOptimizations(code, optimizedCode),
      safetyScore: this._calculateCodeSafety(optimizedCode, contextAnalysis),
      complexity: this._calculateCodeComplexity(optimizedCode)
    };
  }

  /**
   * Learn from code execution results
   */
  learnFromExecution(originalRequest, generatedCode, executionResult, timelineContext) {
    const learningData = {
      timestamp: Date.now(),
      request: originalRequest,
      code: generatedCode.substring(0, 500), // Truncate for storage
      success: executionResult.success,
      executionTime: executionResult.executionTime,
      error: executionResult.error,
      contextSnapshot: this._createContextSnapshot(timelineContext),
      userFeedback: executionResult.userFeedback || null
    };

    this.generationHistory.push(learningData);

    // Limit history size
    if (this.generationHistory.length > 100) {
      this.generationHistory = this.generationHistory.slice(-100);
    }

    // Update prompt templates based on success patterns
    this._updatePromptTemplatesFromLearning(learningData);
  }

  // Private methods

  _initializePromptTemplates() {
    // Basic operation templates
    this.promptTemplates.set('basic_operation', {
      prefix: 'Generate JavaScript code for timeline manipulation that',
      contextFields: ['clips', 'selectedClips', 'duration'],
      complexity: 'simple',
      examples: [
        'timeline.moveClip(clipId, newPosition)',
        'timeline.trimClip(clipId, startTime, endTime)'
      ]
    });

    // Multi-clip operation templates
    this.promptTemplates.set('multi_clip_operation', {
      prefix: 'Generate JavaScript code to perform bulk operations on multiple clips',
      contextFields: ['selectedClips', 'clipTags', 'timeline'],
      complexity: 'medium',
      examples: [
        'selectedClips.forEach(clip => timeline.moveClip(clip.id, newTime))',
        'clips.filter(clip => clip.tags.includes("action")).forEach(...)'
      ]
    });

    // Story sequencing templates
    this.promptTemplates.set('story_sequencing', {
      prefix: 'Generate JavaScript code to arrange clips in a narrative sequence',
      contextFields: ['clips', 'contentAnalysis', 'storyArc'],
      complexity: 'advanced',
      examples: [
        'const storyOrder = ["setup", "conflict", "resolution"]',
        'clips.sort((a, b) => getStoryPosition(a) - getStoryPosition(b))'
      ]
    });

    // Content analysis templates
    this.promptTemplates.set('content_analysis', {
      prefix: 'Generate JavaScript code to analyze and filter timeline content',
      contextFields: ['clips', 'contentTypes', 'qualityMetrics'],
      complexity: 'medium',
      examples: [
        'const bestClips = clips.filter(clip => clip.engagementScore > 0.7)',
        'const contentMap = timeline.analyzeContent()'
      ]
    });

    // Performance optimization templates
    this.promptTemplates.set('performance_optimization', {
      prefix: 'Generate optimized JavaScript code for efficient timeline operations',
      contextFields: ['clipCount', 'duration', 'complexity'],
      complexity: 'advanced',
      examples: [
        'const batchOperations = []',
        'timeline.batchUpdate(() => { /* operations */ })'
      ]
    });
  }

  _initializeContextAnalyzers() {
    // User intent analyzer
    this.contextAnalyzers.set('user_intent', (request, context) => {
      const intent = {
        operation: 'unknown',
        scope: 'single',
        complexity: 'simple',
        urgency: 'normal'
      };

      // Analyze operation type
      if (/move|position|arrange/i.test(request)) intent.operation = 'move';
      else if (/cut|trim|remove/i.test(request)) intent.operation = 'edit';
      else if (/speed|slow|fast/i.test(request)) intent.operation = 'timing';
      else if (/story|sequence|order/i.test(request)) intent.operation = 'sequence';
      else if (/find|search|show/i.test(request)) intent.operation = 'query';

      // Analyze scope
      if (context.selectedClipIds?.length > 1) intent.scope = 'multi';
      else if (/all|every|entire/i.test(request)) intent.scope = 'all';

      // Analyze complexity
      if (/smart|intelligent|auto|best/i.test(request)) intent.complexity = 'advanced';
      else if (/simple|basic|just/i.test(request)) intent.complexity = 'simple';
      else intent.complexity = 'medium';

      // Analyze urgency
      if (/quick|fast|immediately|now/i.test(request)) intent.urgency = 'high';
      else if (/careful|precise|exact/i.test(request)) intent.urgency = 'low';

      return intent;
    });

    // Timeline state analyzer
    this.contextAnalyzers.set('timeline_state', (request, context) => {
      return {
        clipCount: context.clips?.length || 0,
        selectedCount: context.selectedClipIds?.length || 0,
        totalDuration: context.duration || 0,
        hasSelection: (context.selectedClipIds?.length || 0) > 0,
        isComplex: (context.clips?.length || 0) > 10,
        contentTypes: this._analyzeContentTypes(context.clips || []),
        pacingAnalysis: timelineQuerySystem.analyzeTimeline?.(context) || {}
      };
    });

    // User experience analyzer
    this.contextAnalyzers.set('user_experience', (request, context) => {
      const stateManager = timelineStateManager.getEnhancedContext?.() || {};
      
      return {
        editingVelocity: stateManager.editingPatterns?.editingVelocity || 0,
        experienceLevel: this._inferExperienceLevel(stateManager),
        frequentOperations: stateManager.editingPatterns?.frequentOperations || [],
        preferences: stateManager.userPreferences || {},
        sessionDuration: stateManager.sessionInfo?.duration || 0,
        recentErrors: this._getRecentErrors(stateManager)
      };
    });

    // Context complexity analyzer
    this.contextAnalyzers.set('context_complexity', (request, context) => {
      let complexity = 0;
      
      // Add complexity for clip count
      complexity += Math.min((context.clips?.length || 0) * 0.1, 2);
      
      // Add complexity for selection
      if (context.selectedClipIds?.length > 1) complexity += 1;
      
      // Add complexity for content diversity
      const contentTypes = this._analyzeContentTypes(context.clips || []);
      complexity += Math.min(Object.keys(contentTypes).length * 0.3, 1.5);
      
      // Add complexity for request sophistication
      const sophisticatedKeywords = ['smart', 'intelligent', 'optimize', 'analyze', 'sequence'];
      if (sophisticatedKeywords.some(keyword => request.toLowerCase().includes(keyword))) {
        complexity += 1;
      }

      return {
        score: Math.min(complexity, 5),
        level: complexity < 1 ? 'simple' : complexity < 3 ? 'medium' : 'complex',
        factors: this._identifyComplexityFactors(request, context)
      };
    });
  }

  _initializeCodeOptimizers() {
    // Batch operation optimizer
    this.codeOptimizers.set('batch_operations', {
      shouldApply: (context) => context.timelineState?.clipCount > 5,
      optimize: (code, context) => {
        if (code.includes('timeline.') && code.split('timeline.').length > 3) {
          return `timeline.batchUpdate(() => {\n${code}\n});`;
        }
        return code;
      }
    });

    // Error handling optimizer
    this.codeOptimizers.set('error_handling', {
      shouldApply: (context) => context.userExperience?.recentErrors?.length > 0,
      optimize: (code, context) => {
        if (!code.includes('try {')) {
          return `try {\n${code.split('\n').map(line => '  ' + line).join('\n')}\n} catch (error) {\n  console.error('Timeline operation failed:', error);\n}`;
        }
        return code;
      }
    });

    // Performance optimizer
    this.codeOptimizers.set('performance', {
      shouldApply: (context) => context.contextComplexity?.score > 3,
      optimize: (code, context) => {
        // Add performance optimizations for complex operations
        let optimized = code;
        
        // Replace multiple individual operations with batch operations
        if (optimized.includes('forEach')) {
          optimized = `// Performance optimized for ${context.timelineState?.clipCount} clips\n${optimized}`;
        }
        
        return optimized;
      }
    });

    // User preference optimizer
    this.codeOptimizers.set('user_preference', {
      shouldApply: (context) => Object.keys(context.userExperience?.preferences || {}).length > 0,
      optimize: (code, context) => {
        const preferences = context.userExperience?.preferences || {};
        
        // Adjust code style based on user complexity preference
        if (preferences.complexityPreference === 'simple') {
          // Add more comments for simple preference
          return `// ${this._generateSimpleExplanation(code)}\n${code}`;
        } else if (preferences.complexityPreference === 'advanced') {
          // Add advanced features for expert users
          return `${code}\n// Advanced: Consider adding undo point with timeline.createSavePoint()`;
        }
        
        return code;
      }
    });
  }

  _analyzeFullContext(userRequest, timelineContext) {
    const analysis = {};
    
    // Run all context analyzers
    for (const [analyzerName, analyzer] of this.contextAnalyzers) {
      try {
        analysis[analyzerName] = analyzer(userRequest, timelineContext);
      } catch (error) {
        console.warn(`Context analyzer ${analyzerName} failed:`, error);
        analysis[analyzerName] = {};
      }
    }
    
    // Add timeline state manager context
    const stateManagerContext = timelineStateManager.getEnhancedContext?.() || {};
    analysis.stateManager = stateManagerContext;
    
    return analysis;
  }

  _selectPromptStrategy(contextAnalysis, options) {
    const { userIntent, timelineState, contextComplexity } = contextAnalysis;
    
    // Select strategy based on operation type and complexity
    if (userIntent?.operation === 'query') {
      return 'content_analysis';
    } else if (userIntent?.operation === 'sequence') {
      return 'story_sequencing';
    } else if (userIntent?.scope === 'multi' || timelineState?.selectedCount > 1) {
      return 'multi_clip_operation';
    } else if (contextComplexity?.level === 'complex') {
      return 'performance_optimization';
    } else {
      return 'basic_operation';
    }
  }

  _buildEnhancedPrompt(userRequest, contextAnalysis, strategy) {
    const template = this.promptTemplates.get(strategy) || this.promptTemplates.get('basic_operation');
    
    let prompt = `${template.prefix}:\n\n`;
    prompt += `User Request: "${userRequest}"\n\n`;
    
    // Add relevant context
    prompt += this._buildContextSection(contextAnalysis, template.contextFields);
    
    // Add examples if appropriate
    if (template.examples && contextAnalysis.userExperience?.experienceLevel !== 'expert') {
      prompt += `\nExample patterns:\n${template.examples.map(ex => `- ${ex}`).join('\n')}\n`;
    }
    
    return prompt;
  }

  _buildContextSection(contextAnalysis, relevantFields) {
    let context = 'Timeline Context:\n';
    
    const { timelineState, userIntent, contextComplexity } = contextAnalysis;
    
    if (relevantFields.includes('clips')) {
      context += `- Total clips: ${timelineState?.clipCount || 0}\n`;
    }
    
    if (relevantFields.includes('selectedClips') && timelineState?.selectedCount > 0) {
      context += `- Selected clips: ${timelineState.selectedCount}\n`;
    }
    
    if (relevantFields.includes('duration')) {
      context += `- Timeline duration: ${timelineState?.totalDuration || 0} seconds\n`;
    }
    
    if (relevantFields.includes('contentTypes') && timelineState?.contentTypes) {
      const types = Object.keys(timelineState.contentTypes).slice(0, 3);
      context += `- Content types: ${types.join(', ')}\n`;
    }
    
    if (userIntent?.complexity === 'advanced') {
      context += `- Advanced operation requested\n`;
    }
    
    if (contextComplexity?.level === 'complex') {
      context += `- Complex timeline (complexity: ${contextComplexity.score}/5)\n`;
    }
    
    return context + '\n';
  }

  _addContextInstructions(prompt, contextAnalysis) {
    let instructions = prompt;
    
    instructions += 'Code Generation Instructions:\n';
    instructions += '- Generate clean, readable JavaScript code\n';
    instructions += '- Use timeline API methods appropriately\n';
    instructions += '- Include error handling for robustness\n';
    
    // Add context-specific instructions
    const { userIntent, userExperience, contextComplexity } = contextAnalysis;
    
    if (userIntent?.urgency === 'high') {
      instructions += '- Optimize for speed and efficiency\n';
    }
    
    if (userExperience?.experienceLevel === 'beginner') {
      instructions += '- Add explanatory comments\n';
      instructions += '- Use simple, clear variable names\n';
    }
    
    if (contextComplexity?.level === 'complex') {
      instructions += '- Consider performance implications\n';
      instructions += '- Use batch operations where appropriate\n';
    }
    
    if (userExperience?.recentErrors?.length > 0) {
      instructions += '- Add extra validation and error checking\n';
    }
    
    instructions += '\nReturn only the JavaScript code without explanations.\n';
    
    return instructions;
  }

  _generatePatternBasedSuggestions(contextAnalysis) {
    const suggestions = [];
    const patterns = contextAnalysis.userExperience?.frequentOperations || [];
    
    patterns.forEach(operation => {
      switch (operation) {
        case 'speed_adjustment':
          suggestions.push({
            text: 'Speed adjustment',
            command: 'Adjust speed based on your usual preferences',
            category: 'pattern',
            confidence: 0.8
          });
          break;
        case 'arrange_clips':
          suggestions.push({
            text: 'Arrange clips',
            command: 'Arrange clips in your preferred style',
            category: 'pattern',
            confidence: 0.7
          });
          break;
      }
    });
    
    return suggestions;
  }

  _generateContentAwareSuggestions(contextAnalysis) {
    const suggestions = [];
    const contentTypes = contextAnalysis.timelineState?.contentTypes || {};
    
    if (contentTypes.cooking) {
      suggestions.push({
        text: 'Create cooking story',
        command: 'Arrange cooking clips in story order',
        category: 'content',
        confidence: 0.9
      });
    }
    
    if (contentTypes.action) {
      suggestions.push({
        text: 'Create action montage',
        command: 'Create high-energy montage from action clips',
        category: 'content',
        confidence: 0.8
      });
    }
    
    return suggestions;
  }

  _generateOptimizationSuggestions(contextAnalysis) {
    const suggestions = [];
    const timelineState = contextAnalysis.timelineState || {};
    
    if (timelineState.totalDuration > 120) {
      suggestions.push({
        text: 'Optimize duration',
        command: 'Cut timeline to optimal length',
        category: 'optimization',
        confidence: 0.7
      });
    }
    
    if (timelineState.clipCount > 10) {
      suggestions.push({
        text: 'Reduce clip count',
        command: 'Merge or remove redundant clips',
        category: 'optimization',
        confidence: 0.6
      });
    }
    
    return suggestions;
  }

  _generatePreferenceBasedSuggestions(contextAnalysis) {
    const suggestions = [];
    const preferences = contextAnalysis.userExperience?.preferences || {};
    
    if (preferences.preferredDuration) {
      suggestions.push({
        text: `Cut to ${preferences.preferredDuration}s`,
        command: `Cut timeline to your preferred ${preferences.preferredDuration} seconds`,
        category: 'preference',
        confidence: 0.9
      });
    }
    
    if (preferences.complexityPreference === 'advanced') {
      suggestions.push({
        text: 'Advanced sequence',
        command: 'Create sophisticated story sequence',
        category: 'preference',
        confidence: 0.8
      });
    }
    
    return suggestions;
  }

  _rankAndFilterSuggestions(suggestions, options) {
    const maxSuggestions = options.maxSuggestions || 6;
    const minConfidence = options.minConfidence || 0.5;
    
    return suggestions
      .filter(s => s.confidence >= minConfidence)
      .sort((a, b) => b.confidence - a.confidence)
      .slice(0, maxSuggestions);
  }

  _analyzeContentTypes(clips) {
    const types = {};
    clips.forEach(clip => {
      clip.tags?.forEach(tag => {
        types[tag] = (types[tag] || 0) + 1;
      });
    });
    return types;
  }

  _inferExperienceLevel(stateManagerContext) {
    const editCount = stateManagerContext.editingHistory?.length || 0;
    const velocity = stateManagerContext.editingPatterns?.editingVelocity || 0;
    
    if (editCount > 50 && velocity > 2) return 'expert';
    if (editCount > 20 && velocity > 1) return 'intermediate';
    return 'beginner';
  }

  _getRecentErrors(stateManagerContext) {
    const history = stateManagerContext.editingHistory || [];
    return history
      .filter(entry => !entry.success)
      .slice(-3) // Last 3 errors
      .map(entry => entry.error);
  }

  _identifyComplexityFactors(request, context) {
    const factors = [];
    
    if ((context.clips?.length || 0) > 10) factors.push('high_clip_count');
    if ((context.selectedClipIds?.length || 0) > 5) factors.push('large_selection');
    if (/smart|intelligent|optimize/i.test(request)) factors.push('sophisticated_request');
    if (context.duration > 180) factors.push('long_duration');
    
    return factors;
  }

  _calculatePromptConfidence(contextAnalysis, strategy) {
    let confidence = 0.5; // Base confidence
    
    // Increase confidence based on context completeness
    if (contextAnalysis.userIntent?.operation !== 'unknown') confidence += 0.2;
    if (contextAnalysis.timelineState?.clipCount > 0) confidence += 0.1;
    if (contextAnalysis.userExperience?.experienceLevel === 'expert') confidence += 0.1;
    
    // Adjust based on strategy appropriateness
    if (strategy !== 'basic_operation') confidence += 0.1;
    
    return Math.min(confidence, 1.0);
  }

  _estimateTaskComplexity(contextAnalysis) {
    const complexity = contextAnalysis.contextComplexity?.score || 1;
    
    if (complexity < 2) return 'simple';
    if (complexity < 4) return 'medium';
    return 'complex';
  }

  _suggestAIModel(contextAnalysis) {
    const complexity = this._estimateTaskComplexity(contextAnalysis);
    
    switch (complexity) {
      case 'simple': return 'gpt-3.5-turbo';
      case 'medium': return 'gpt-4';
      case 'complex': return 'gpt-4';
      default: return 'gpt-4';
    }
  }

  _generateFallbackPrompt(userRequest, timelineContext) {
    return {
      prompt: `Generate JavaScript code for timeline manipulation:\n\nUser Request: "${userRequest}"\n\nTimeline has ${timelineContext.clips?.length || 0} clips.\n\nGenerate clean JavaScript code using timeline API methods.`,
      strategy: 'fallback',
      contextAnalysis: {},
      confidence: 0.3,
      estimatedComplexity: 'simple',
      suggestedModel: 'gpt-3.5-turbo'
    };
  }

  _recordGeneration(userRequest, contextAnalysis, strategy, prompt) {
    // Record for learning and improvement
    console.debug('Smart code generation:', {
      request: userRequest,
      strategy,
      complexity: contextAnalysis.contextComplexity?.level,
      promptLength: prompt.length
    });
  }

  _addContextSafetyChecks(code, contextAnalysis) {
    // Add appropriate safety checks based on context
    if (contextAnalysis.userExperience?.recentErrors?.length > 0) {
      return `// Added safety checks due to recent errors\nif (timeline && timeline.getClips) {\n${code}\n} else {\n  console.error('Timeline not available');\n}`;
    }
    return code;
  }

  _addSmartErrorHandling(code, contextAnalysis) {
    if (contextAnalysis.userExperience?.experienceLevel === 'beginner' && !code.includes('try {')) {
      return `try {\n${code.split('\n').map(line => '  ' + line).join('\n')}\n} catch (error) {\n  console.error('Operation failed:', error.message);\n  // Consider reverting changes or showing user-friendly message\n}`;
    }
    return code;
  }

  _getAppliedOptimizations(originalCode, optimizedCode) {
    const optimizations = [];
    
    if (optimizedCode.includes('batchUpdate') && !originalCode.includes('batchUpdate')) {
      optimizations.push('batch_operations');
    }
    
    if (optimizedCode.includes('try {') && !originalCode.includes('try {')) {
      optimizations.push('error_handling');
    }
    
    if (optimizedCode.includes('// Performance') && !originalCode.includes('// Performance')) {
      optimizations.push('performance');
    }
    
    return optimizations;
  }

  _calculateCodeSafety(code, contextAnalysis) {
    let safety = 1.0;
    
    // Reduce safety for potentially dangerous operations
    if (code.includes('timeline.clear()')) safety -= 0.3;
    if (code.includes('removeClip') && !code.includes('confirm')) safety -= 0.2;
    
    // Increase safety for defensive programming
    if (code.includes('try {')) safety += 0.1;
    if (code.includes('if (timeline')) safety += 0.1;
    
    return Math.max(0, Math.min(1, safety));
  }

  _calculateCodeComplexity(code) {
    const lines = code.split('\n').length;
    const loops = (code.match(/for\s*\(|forEach|while/g) || []).length;
    const conditionals = (code.match(/if\s*\(/g) || []).length;
    const apiCalls = (code.match(/timeline\./g) || []).length;
    
    return Math.min(10, lines * 0.1 + loops * 0.5 + conditionals * 0.3 + apiCalls * 0.2);
  }

  _createContextSnapshot(timelineContext) {
    return {
      clipCount: timelineContext.clips?.length || 0,
      selectedCount: timelineContext.selectedClipIds?.length || 0,
      duration: timelineContext.duration || 0,
      contentTypes: Object.keys(this._analyzeContentTypes(timelineContext.clips || [])).slice(0, 3)
    };
  }

  _updatePromptTemplatesFromLearning(learningData) {
    // Update templates based on successful patterns
    // This could be expanded to use machine learning techniques
    if (learningData.success && learningData.executionTime < 1000) {
      console.debug('Successful pattern recorded for learning');
    }
  }

  _generateSimpleExplanation(code) {
    if (code.includes('moveClip')) return 'Moving clips to new positions';
    if (code.includes('trimClip')) return 'Cutting clips to specific lengths';
    if (code.includes('removeClip')) return 'Removing clips from timeline';
    if (code.includes('forEach')) return 'Applying operation to multiple clips';
    return 'Performing timeline operation';
  }
}

// Export singleton instance
const smartCodeGenerator = new SmartCodeGenerator();
export default smartCodeGenerator;